// src/components/quality/AssessmentCard.js
import { useState, useEffect } from 'react';
import useAuth from '../../hooks/useAuth';
import api from '../../services/api';
import QualityAssessmentModal from './QualityAssessmentModal';

const AssessmentCard = ({ assessment, projectSelect, onEdit, onAssessmentDeleted }) => {
  const { user, isAdmin } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [soundIdForModal, setSoundIdForModal] = useState(null);
  const [selections, setSelections] = useState([]);

  // Загрузка выборок при монтировании
  useEffect(() => {
    const fetchSelections = async () => {
      try {
        const res = await api.get('/quality/assessment-selection');
        setSelections(res.data.data || []);
      } catch (err) {
        console.error('Ошибка загрузки выборок:', err);
        setSelections([]);
      }
    };
    fetchSelections();
  }, []);

  const isInSelection = (assessmentId) => {
    return selections.some(s => s.quality_assessment_id === assessmentId);
  };

  const handleToggleSelection = async () => {
    const assessmentId = assessment.id;
    const projectId = assessment.project_id;

    const existingSelection = selections.find(s => s.quality_assessment_id === assessmentId);

    if (existingSelection) {
      try {
        await api.delete(`/quality/assessment-selection/${existingSelection.id}`);
        setSelections(prev => prev.filter(s => s.id !== existingSelection.id));
        window.showToast('Оценка удалена из анализа');
      } catch (error) {
        console.error('Ошибка при удалении из анализа:', error);
        window.showToast('Не удалось удалить оценку из анализа');
      }
    } else {
      try {
        const response = await api.post('/quality/assessment-selection', {
          quality_assessment_id: assessmentId,
          project_id: projectId,
          added_by_id: user.id
        });
        setSelections(prev => [response.data, ...prev]);
        window.showToast('Оценка добавлена в анализ');
      } catch (error) {
        console.error('Ошибка при добавлении в анализ:', error);
        window.showToast('Не удалось добавить оценку в анализ');
      }
    }
  };

  // Проверка прав на редактирование
  const roleNames = new Set(user?.roles?.map(r => r.name) || []);
  console.log('roleNames: ', roleNames)
  const canEdit = user.superuser === true ||
                 (roleNames.has('admin')) ||
                 (roleNames.has('auditor') && !roleNames.has('admin') && user.id === assessment.assessor_id);
  
  // Проверка прав на удаление (только для superuser)
  const canDelete = user.superuser === true;

  const toggleExpand = () => setExpanded(!expanded);

  const handleDeleteAssessment = async (assessment) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту оценку? Это действие нельзя отменить.')) {
      return;
    }
    
    try {
      await api.delete(`/quality/assessment/${assessment.id}`);
      // Обновляем список оценок через callback
      onAssessmentDeleted();
    } catch (error) {
      console.error('Ошибка при удалении оценки:', error);
      window.showToast('Не удалось удалить оценку');
    }
  };
  // console.log(assessment)
  // Логирование для отладки
  return (
    <div className="assessment-card">
      <div className="card-header" onClick={toggleExpand} style={{cursor: 'pointer'}}>
        <h4>{assessment.agent_name}</h4>
        <small>
          {assessment.created_at ? (() => {
            const date = new Date(assessment.created_at);
            date.setHours(date.getHours() + 3);
            return date.toLocaleString('ru-RU', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            });
          })() : '—'}
          {assessment.total_score < 1.0 ?
              <span
                  className={assessment.feedback && assessment.feedback.trim() !== '' ? 'badge bg-success' : 'badge bg-danger'}
                  style={{marginLeft: '10px', fontSize: '0.8em', padding: '0.25em 0.4em'}}>
            {assessment.feedback && assessment.feedback.trim() !== '' ? 'Дана ОС' : 'Нет ОС'}
          </span>
              : ''
          }

          <span style={{marginLeft: '10px', fontSize: '0.9em', color: '#666'}}>
            {(assessment.total_score * 100).toFixed(0)} %
          </span>
        </small>
        {assessment.sound_id && (
          <div style={{display: 'flex', alignItems: 'center', gap: '5px', marginTop: '5px'}}>
            <span style={{fontSize: '0.85em', color: '#666'}}>
              <strong>ID звонка:</strong> {assessment.sound_id}
            </span>
            <button 
              type="button" 
              className="btn btn-sm btn-primary"
              onClick={(e) => {
                e.stopPropagation();
                setSoundIdForModal(assessment.sound_id);
              }}
            >
              Посмотреть оценку
            </button>
            {canEdit && (
                <button
                    className={`btn btn-sm ${isInSelection(assessment.id) ? 'btn-danger' : 'btn-success'}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleSelection();
                    }}
                    type="button"
                >
                  {isInSelection(assessment.id) ? 'Убрать из анализа' : 'Добавить в анализ'}
                </button>
            )
            }
          </div>
        )}
        <div>
          <strong>Проверяющий:</strong> {assessment.assessor ? assessment.assessor.surname ? `${assessment.assessor.surname} ${assessment.assessor.name}` : assessment.assessor.username : '—'}
        </div>
        {canEdit && (
            <button className="btn btn-sm btn-secondary" onClick={(e) => {
              e.stopPropagation();
              onEdit(assessment);
            }}>
              Редактировать
            </button>
        )}
        {canDelete && (
            <button className="btn btn-sm btn-danger" onClick={(e) => {
              e.stopPropagation();
              handleDeleteAssessment(assessment);
            }}>
              Удалить
            </button>
        )}

        <span>{expanded ? '▲' : '▼'}</span>
      </div>

      {soundIdForModal && (
        <QualityAssessmentModal
          isOpen={true}
          onClose={() => setSoundIdForModal(null)}
          sound_id={soundIdForModal}
        />
      )}

      {expanded && (
        <div className="card-body">
          <div className="info-grid">

            <div><strong>Группа:</strong> {assessment.group || '—'}</div>
            <div><strong>Канал поступления:</strong> {assessment.receipt_channel || '—'}</div>
            <div><strong>Impact:</strong> {assessment.impact || '—'}</div>
            <div><strong>SRQ:</strong> {assessment.srq || '—'}</div>
            <div><strong>Сервис:</strong> {assessment.service || '—'}</div>
            <div><strong>Статус:</strong> {assessment.status}</div>
            {assessment.url_sound && (
              <div><strong>Ссылка на звонок:</strong>
                <a href={assessment.url_sound} target="_blank" rel="noopener noreferrer">
                  {' ' + assessment.url_sound}
                </a>
              </div>
            )}
            {(assessment.url_ticket!=='' && assessment.url_ticket) ? (
              <div><strong>Ссылка на тикет:</strong>
                <a href={assessment.url_ticket} target="_blank" rel="noopener noreferrer">
                  {' ' + assessment.url_ticket}
                </a>
              </div>
            ) : (assessment.url_sound!=='' && assessment.url_sound) ? (
                <div><strong>Ссылка на тикет:</strong>
                  <a href={assessment.url_sound} target="_blank" rel="noopener noreferrer">
                    {' ' + assessment.url_sound}
                  </a>
                </div>
            ) : (
                <div><strong>Ссылка на тикет:</strong>
                  Нет ссылки
                </div>
            )}
              <div><strong>ОС при обработке:</strong> {assessment.feedback || '—'}</div>
              <div><strong>Специалист ОС:</strong> {assessment.os_specialist || '—'}</div>
              <div><strong>Subject:</strong> {assessment.subject2 || '—'}</div>
              <div><strong>Комментарий:</strong> {assessment.comment || '—'}</div>
          </div>

          <h5>Критерии оценки</h5>
          <table className="criteria-table">
            <thead>
              <tr>
                <th>Критерий</th>
                <th>Оценка</th>
                <th>Баллы</th>
                <th>Вес</th>
              </tr>
            </thead>
            <tbody>
              {assessment.items.map((item) => (
                <tr key={item.criteria_id} style={{ backgroundColor: (item.critical && item.score===0.0) ? '#ffdddd' : 'transparent' }}>
                  <td>{item.criteria_name}</td>
                  <td>{item.option_text}</td>
                  <td>{item.score}</td>
                  <td>{item.weight}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="2"><strong>Итого:</strong></td>
                <td><strong>{(assessment.total_score*100).toFixed(0)} %</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default AssessmentCard;